import { N8nModule } from '../../types';

export const module2: N8nModule = {
  id: 'zapier-multi-step',
  title: 'Multi-Step Zaps and Filters',
  description: 'Build complex workflows with multiple actions and conditional logic.',
  content: `# Multi-Step Zaps and Filters

## Building Multi-Step Workflows

Multi-step Zaps allow you to create sophisticated automations by chaining multiple actions together.

### Adding Multiple Actions

After your trigger, you can add as many actions as needed:

1. **Sequential Processing**
   - Actions run in order from top to bottom
   - Each action can use data from previous steps
   - Build complex workflows step-by-step

2. **Data Transformation**
   - Format data between steps
   - Use Formatter by Zapier
   - Clean and structure information

## Filters and Conditional Logic

Filters control when your Zaps run by adding conditions.

### Filter Basics

- **Only Continue If**: Specify conditions that must be met
- **AND/OR Logic**: Combine multiple conditions
- **Field Comparisons**: Compare values, check for text, verify numbers

### Common Filter Examples

\`\`\`
Filter Examples:
- Email subject contains "urgent"
- Amount is greater than 1000
- Status equals "New Lead"
- Date is within the last 7 days
\`\`\`

## Paths (Multiple Branches)

Paths let your Zap take different routes based on conditions.

### Setting Up Paths

1. **Add a Paths Step**
   - Create multiple branches
   - Each path has its own conditions
   - Paths are mutually exclusive

2. **Path Configuration**
   - Define rules for each path
   - Set up different actions per path
   - Include a default path for unmatched conditions

### Path Example

\`\`\`
Trigger: New Form Submission
  Path A: If Country = "USA"
    → Add to US CRM
    → Send US Welcome Email
  Path B: If Country = "UK"
    → Add to UK CRM
    → Send UK Welcome Email
  Path C: All Other Countries
    → Add to Global CRM
    → Send Standard Welcome Email
\`\`\`

## Data Formatting

Transform data between steps using built-in tools.

### Formatter by Zapier

Available transformations:
- **Text**: Split, replace, trim, capitalize
- **Numbers**: Perform math operations, format
- **Date/Time**: Parse, format, calculate
- **Utilities**: Pick from list, line items

### Example Transformations

1. **Text Formatting**
   - Convert to uppercase/lowercase
   - Extract specific parts
   - Find and replace text

2. **Date Operations**
   - Add/subtract time
   - Change formats
   - Convert time zones

3. **Number Calculations**
   - Add, subtract, multiply, divide
   - Round numbers
   - Format currency

## Advanced Techniques

### Lookup Tables

Store reference data for dynamic lookups:
- Match values to actions
- Use spreadsheets as lookup tables
- Create dynamic responses

### Delays

Add time delays between actions:
- **Delay For**: Wait a specific amount of time
- **Delay Until**: Wait until a specific date/time

### Webhooks

Send and receive custom HTTP requests:
- POST data to APIs
- Receive webhook triggers
- Parse JSON responses

## Best Practices

1. **Plan Your Workflow**: Map out steps before building
2. **Use Filters Wisely**: Only process relevant data
3. **Test Each Path**: Verify all branches work correctly
4. **Monitor Performance**: Check task usage and efficiency
5. **Document Complex Zaps**: Add notes for future reference`,
  duration: '120 minutes',
  order: 2,
  objectives: [
    'Create multi-step workflows',
    'Implement filters and conditional logic',
    'Use Paths for branching workflows',
    'Transform data with Formatter',
    'Build complex automation logic'
  ],
  keyConcepts: [
    'Multi-step Zaps',
    'Filters and conditions',
    'Paths and branching',
    'Data formatting',
    'Webhooks and delays'
  ]
};
